import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from 'styled-components';
// template components
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// styles
import {
  ColStyled,
  TableStyled,
} from '../tableStyles/styles';

export default () => {

  const theme = useContext(ThemeContext);
  const mediumUp = theme.mediumUp();
  const [isMediumUp, setIsMediumUp] = useState(mediumUp);
  const defaultTableHeadingWidth = '100px';
  const [tableHeadingWidth, setTableHeadingWidth] = useState(defaultTableHeadingWidth);

  const handleResize = (bool) => {
    setIsMediumUp(bool);
  };

  useEffect(() => {
    handleResize(mediumUp);
  }, [mediumUp]);

  useEffect(() => {
    setTableHeadingWidth(isMediumUp ? '275px' : defaultTableHeadingWidth);
  }, [isMediumUp]);

  const tableHeadingCol1 = 'Mailing dates';
  const tableHeadingCol2 = 'Tax forms';

  const getTableHeading = (headingText) => {
    return (
      <Typography
        weight={400}
        themeType='darkTheme'
        disableBottomPadding>
        {headingText}
      </Typography>
    );
  };

  const getDateHeading = (date) => {
    return (
      <Typography
        weight={400}
        disableBottomPadding>
        {date}
      </Typography>
    );
  };

  return (
    <SectionContainer
      id='tax-documents-mailing-schedules'
      backgroundVariant='lightC'
      themeType='lightTheme'>
      <Row align='center'>
        <Col>
          <Typography
            component='h2'
            align='center'
            nextElement='typography'>
            Mailing schedules for our <WidowBlock>tax forms</WidowBlock>
          </Typography>
          <Typography
            component='h3'
            align='center'
            nextElement='typography'>
            Life insurance &#38; annuities
          </Typography>
        </Col>
      </Row>
      {/* start table 1 */}
      <Row align='center'>
        <ColStyled>
          <TableStyled
            variant='branded'
            alignTableContentCenter
            tableData={{
              summary: <>This table shows mailing dates for life insurance &#38; annuities tax forms</>,
              tableHead: {
                rows: [
                  {
                    cols: [
                      {
                        children: getTableHeading(tableHeadingCol1),
                        width: tableHeadingWidth,
                      },
                      {
                        children: getTableHeading(tableHeadingCol2),
                        colScope: 'colgroup',
                      },
                    ],
                  },
                ],
              },
              tableBody: {
                displayStripedRows: false,
                rows: [
                  {
                    cols: [
                      {
                        children: getDateHeading('January 31'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <Typography
                            weight={400}
                            disableBottomPadding>
                            1099
                          </Typography>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('March 15'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <Typography
                            weight={400}
                            disableBottomPadding>
                            1042
                          </Typography>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('May 31'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <Typography
                            weight={400}
                            disableBottomPadding>
                            5498
                          </Typography>
                        ),
                      },
                    ],
                  },
                ],
              },
            }}
          />
        </ColStyled>
      </Row>
      {/* start table 2 */}
      <Row align='center'>
        <Col medium={8}>
          <Typography
            component='h3'
            align='center'
            nextElement='typography'>
            Investments &#38; retirement accounts
          </Typography>
          <Typography nextElement='typography'>
            Throughout tax season, income reclassifications and tax information are received at various times from issuers. Your tax forms will be sent to you once the information is received.
          </Typography>
        </Col>
        <ColStyled>
          <TableStyled
            variant='branded'
            tableData={{
              summary: <>This table shows mailing dates for investments &#38; retirement accounts tax forms</>,
              tableHead: {
                rows: [
                  {
                    cols: [
                      {
                        children: getTableHeading(tableHeadingCol1),
                        width: tableHeadingWidth,
                      },
                      {
                        children: getTableHeading(tableHeadingCol2),
                        colScope: 'colgroup',
                      },
                    ],
                  },
                ],
              },
              tableBody: {
                rows: [
                  {
                    cols: [
                      {
                        children: getDateHeading('January 31'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <Typography
                              weight={500}
                              disableBottomPadding>
                              1099&#45;R
                            </Typography>
                            <Typography
                              weight={400}
                              disableBottomPadding>
                              Retirement account &#40;IRA&#41; distributions over &#36;10
                            </Typography>
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('January 31'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <Typography
                              weight={500}
                              disableBottomPadding>
                              1099&#45;Q
                            </Typography>
                            <Typography
                              weight={400}
                              disableBottomPadding>
                              Qualified Education Account (529, Coverdell, ESA) payments including plan withdrawals, exchanges and&#47;or allocation changes
                            </Typography>
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('January 31'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <Typography
                              weight={500}
                              disableBottomPadding>
                              Phase I Composite 1099 (Tax &#38; Year End Statement)
                            </Typography>
                            <Typography
                              weight={400}
                              disableBottomPadding>
                              Accounts with simple tax information and no pending income reclassifications
                            </Typography>
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('February 14'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <Typography
                              weight={500}
                              disableBottomPadding>
                              Phase II Composite 1099 (Tax &#38; Year End Statement)
                            </Typography>
                            <Typography
                              weight={400}
                              disableBottomPadding>
                              Accounts with finalized income reclassifications received and&#47;or the issuer since the previous phase
                            </Typography>
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('February 21'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <Typography
                              weight={500}
                              disableBottomPadding>
                              Phase III Composite 1099 (Tax &#38; Year End Statement)
                            </Typography>
                            <Typography
                              weight={400}
                              disableBottomPadding>
                              Accounts with finalized income reclassifications received and&#47;or the issuer since the previous phase
                            </Typography>
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('February 28'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <Typography
                              weight={500}
                              disableBottomPadding>
                              Phase IV Composite 1099 (Tax &#38; Year End Statement)
                            </Typography>
                            <Typography
                              weight={400}
                              disableBottomPadding>
                              Accounts with finalized income reclassifications received and&#47;or the issuer since the previous phase
                            </Typography>
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('March 7'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <Typography
                              weight={500}
                              disableBottomPadding>
                              Phase V Composite 1099 (Tax &#38; Year End Statement)
                            </Typography>
                            <Typography
                              weight={400}
                              disableBottomPadding>
                              Accounts with finalized income reclassifications received and&#47;or the issuer since the previous phase
                            </Typography>
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('March 14'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <Typography
                              weight={500}
                              disableBottomPadding>
                              Phase VI Composite 1099 (Tax &#38; Year End Statement)
                            </Typography>
                            <Typography
                              weight={400}
                              disableBottomPadding>
                              All accounts not included in previous mailings. Accounts holding certain complex debt, non&#45;equity securities (REMICs, WHFITs), and some UITs.
                            </Typography>
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getDateHeading('May 31'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <Typography
                              weight={500}
                              disableBottomPadding>
                              5498
                            </Typography>
                            <Typography
                              weight={400}
                              disableBottomPadding>
                              Retirement accounts (IRA) contributions (including rollovers) and year&#45;end fair market value (FMV)
                            </Typography>
                          </>
                        ),
                      },
                    ],
                  },
                ],
              },
            }}
          />
        </ColStyled>
      </Row>
    </SectionContainer>
  );
};
